import { useState } from 'react'
import { Form, Input, Button, Card, Typography, Space, Checkbox, Divider, Alert } from 'antd'
import { MailOutlined, LockOutlined, GoogleOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { apiService, AuthCredentials } from '@/lib/api'

const { Title, Text } = Typography

export default function LoginPage() {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const loginMutation = useMutation({
    mutationFn: (credentials: AuthCredentials) => apiService.login(credentials),
    onSuccess: async (response) => {
      if (response.success && response.user && response.token) {
        setSuccessMessage('Login successful!')
        setErrorMessage(null)
        localStorage.setItem('authToken', response.token)

        try {
          const profileResponse = await apiService.getProfile()
          if (profileResponse.success && profileResponse.user) {
            const userData = {
              ...profileResponse.user,
              needs_onboarding: profileResponse.needs_onboarding
            }
            localStorage.setItem('user', JSON.stringify(userData))

            if (profileResponse.needs_onboarding) {
              navigate('/onboarding')
            } else {
              navigate('/dashboard')
            }
          } else {
            localStorage.setItem('user', JSON.stringify(response.user))
            navigate('/dashboard')
          }
        } catch (error) {
          localStorage.setItem('user', JSON.stringify(response.user))
          navigate('/dashboard')
        }
      } else {
        const error = response.error || response.message || response.errors?.join(', ') || 'Login failed. Please check your credentials.'
        setErrorMessage(error)
        setSuccessMessage(null)
      }
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Login failed. Please check your credentials.')
      setSuccessMessage(null)
    },
  })

  const handleGoogleLogin = async () => {
    try {
      await apiService.initGoogleOAuth()
    } catch (error) {
      setErrorMessage('Failed to initiate Google login')
    }
  }

  const onFinish = (values: AuthCredentials & { remember?: boolean }) => {
    const { remember, ...credentials } = values
    loginMutation.mutate(credentials)
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" style={{ background: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="text-center mb-6">
          <Title level={2} className="mb-2">Welcome Back</Title>
          <Text type="secondary">Please sign in to your account</Text>
        </div>

        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
            className="mb-4"
          />
        )}

        {successMessage && (
          <Alert
            message={successMessage}
            type="success"
            showIcon
            className="mb-4"
          />
        )}

        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          initialValues={{ remember: true }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Enter your email" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input your password!' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" />
          </Form.Item>

          <Form.Item>
            <div className="flex justify-between items-center">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>Remember me</Checkbox>
              </Form.Item>
              <Link to="/forgot-password" className="text-cyan-400 hover:text-cyan-300">
                Forgot password?
              </Link>
            </div>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              loading={loginMutation.isPending}
            >
              {loginMutation.isPending ? 'Signing in...' : 'Sign In'}
            </Button>
          </Form.Item>
        </Form>

        <Divider>or</Divider>

        <Button
          type="default"
          icon={<GoogleOutlined />}
          onClick={handleGoogleLogin}
          className="w-full mb-4"
          size="large"
        >
          Sign in with Google
        </Button>

        <div className="text-center">
          <Space>
            <Text type="secondary">Don't have an account?</Text>
            <Link to="/signup" className="text-cyan-400 hover:text-cyan-300">
              Sign up
            </Link>
          </Space>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-400 hover:text-slate-300">
            ← Back to Home
          </Link>
        </div>
      </Card>
    </div>
  )
}
